import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Typography,
  Dropdown,
  Tag,
  App,
  Empty,
} from "antd";
import { EllipsisOutlined, LeftOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  deleteLineThunk,
  getLinesThunk,
  updateLineThunk,
} from "../../store/actions/line.action";
import { PhoneService, LineStatus } from "../../models/line.model";
import CreateLineModal from "./CreateLineModal";
import { SortOrder } from "antd/es/table/interface";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import dayjs from "dayjs";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ROLES } from "../../models/user.model";

const ManagePhoneNumber: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { lines, loading } = useAppSelector((state) => state.line);
  const pageSize = 10;
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sorter, setSorter] = useState<{
    field: "createdAt";
    order: SortOrder | undefined;
  }>({ field: "createdAt", order: "descend" });
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const { user } = useAppSelector((state) => state.auth);

  const columns = [
    {
      title: t("settings.managePhoneNumber.phoneNumber"),
      dataIndex: "line_number",
      key: "line_number",
      render: (text: string) => (
        <span style={{ fontWeight: 500, color: "#000" }}>{text}</span>
      ),
    },
    {
      title: t("settings.managePhoneNumber.service"),
      dataIndex: "phone_service",
      key: "phone_service",
      render: (service: PhoneService) => getServiceText(service),
    },
    {
      title: t("settings.managePhoneNumber.status"),
      dataIndex: "status",
      key: "status",
      render: (status: number) => getStatusText(status),
    },
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: sorter.field === "createdAt" ? "#000" : "inherit",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCustomSort("createdAt");
          }}
        >
          {t("settings.managePhoneNumber.createdTime")}{" "}
          {sorter.field === "createdAt" &&
            (sorter.order === "ascend" ? (
              <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
            ) : (
              <ArrowDownIcon
                style={{ marginLeft: 4, verticalAlign: "middle" }}
              />
            ))}
        </span>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: false,
      sortOrder: undefined,
      onHeaderCell: () => ({ style: { cursor: "pointer" } }),
    },
    {
      title: "",
      key: "action",
      render: (_: any, record: any) => {
        const isPaused = record.status === LineStatus.PAUSED;
        const isActive = record.status === LineStatus.ACTIVE;

        const menuItems = [];

        if (isPaused) {
          menuItems.push({
            key: "active",
            label: t("settings.managePhoneNumber.active"),
            onClick: () => handleToggleLineStatus(record.key, record.status),
          });
        } else if (isActive) {
          menuItems.push({
            key: "pause",
            label: t("settings.managePhoneNumber.pause"),
            onClick: () => handleToggleLineStatus(record.key, record.status),
          });
        }

        // Don't show dropdown if no actions available
        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown
            menu={{
              items: menuItems,
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // Get service display text
  const getServiceText = (service: PhoneService) => {
    switch (service) {
      case PhoneService.CALLS_ONLY:
        return t("settings.managePhoneNumber.services.callsOnly");
      case PhoneService.CALLS_SMS:
        return t("settings.managePhoneNumber.services.callsAndMessages");
      default:
        return t("settings.managePhoneNumber.services.unknown");
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case LineStatus.ACTIVE:
        return t("settings.managePhoneNumber.statuses.active");
      case LineStatus.PAUSED:
        return t("settings.managePhoneNumber.statuses.paused");
      case LineStatus.MESSAGE_SERVICE_PENDING:
        return t("settings.managePhoneNumber.statuses.messageServicePending");
      default:
        return t("settings.managePhoneNumber.statuses.unknown");
    }
  };

  const fetchLines = async (
    page = currentPage,
    keyword = debouncedSearch,
    sortField: "createdAt" = sorter.field,
    sortOrder?: SortOrder
  ) => {
    try {
      const sortBy: Record<string, 1 | -1> = {};
      sortBy[sortField] = sortOrder === "ascend" ? 1 : -1;

      await dispatch(
        getLinesThunk({
          limit: pageSize,
          page,
          line_number: keyword,
          sort: Object.keys(sortBy).length ? sortBy : undefined,
        })
      ).unwrap();
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleCustomSort = (field: "createdAt") => {
    let nextOrder: SortOrder = "descend";
    if (sorter.field === field) {
      nextOrder = sorter.order === "ascend" ? "descend" : "ascend";
    }
    setSorter({ field, order: nextOrder });
    fetchLines(currentPage, debouncedSearch, field, nextOrder);
  };

  const handleToggleLineStatus = async (id: string, currentStatus: number) => {
    try {
      const newStatus =
        currentStatus === LineStatus.ACTIVE
          ? LineStatus.PAUSED
          : LineStatus.ACTIVE;

      await dispatch(
        updateLineThunk({ id, payload: { status: newStatus } })
      ).unwrap();

      fetchLines(1, debouncedSearch);

      const isActivating = newStatus === LineStatus.ACTIVE;
      const successMessage = isActivating
        ? t("settings.managePhoneNumber.lineActivatedSuccessfully")
        : t("settings.managePhoneNumber.linePausedSuccessfully");

      toast.success(successMessage, 5, <CheckCircleIcon />);
    } catch (error) {
      const isActivating = currentStatus === LineStatus.PAUSED;
      const errorMessage = isActivating
        ? t("settings.managePhoneNumber.failedToActivateLine")
        : t("settings.managePhoneNumber.failedToPauseLine");

      toast.error(errorMessage, 5);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  const handleCreateNumber = () => {
    setIsCreateModalOpen(true);
  };

  useEffect(() => {
    fetchLines(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);

  return (
    <Container>
      <BackLink onClick={() => navigate(-1)}>
        <LeftOutlined style={{ marginRight: 8 }} />{" "}
        {t("settings.managePhoneNumber.backToSettings")}
      </BackLink>
      <HeaderSection>
        <Title>{t("settings.managePhoneNumber.title")}</Title>
        {user?.role === ROLES.ADMIN && (
          <CreateButton onClick={handleCreateNumber}>
            <AddIcon /> {t("settings.managePhoneNumber.createNewNumber")}
          </CreateButton>
        )}
      </HeaderSection>

      <CardWrapper>
        <SearchSection>
          <SearchWrapper>
            <SearchIcon />
            <CustomSearch
              placeholder={t("settings.managePhoneNumber.searchPhoneNumber")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchWrapper>
        </SearchSection>

        <StyledTable
          dataSource={lines.lines.map((line) => ({
            key: line._id,
            line_number: line.line_number,
            phone_service: line.phone_service,
            status: line.status,
            createdAt: dayjs(line.createdAt).format("MMM D, YYYY"),
          }))}
          columns={columns}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: lines.pagination.totalItems,
            position: ["bottomCenter"],
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
          }}
          rowKey="key"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <EmptyStateTitle>{t("common.noData")}</EmptyStateTitle>
                }
              />
            ),
          }}
        />
      </CardWrapper>
      <CreateLineModal
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onSuccess={() => fetchLines(1, debouncedSearch)}
      />
    </Container>
  );
};

export default ManagePhoneNumber;

// Styled Components
const Container = styled.div`
  padding: 52px 72px;
  background-color: ${theme.colors.grey3};
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const BackLink = styled.div`
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 16px;

  &:hover {
    background: #047857;
  }
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  background: white;
`;

const SearchSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const SearchWrapper = styled.div`
  width: 240px;
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0px 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }

  .ant-table-pagination {
    margin: 16px 0 0 0;
  }
`;
